import { auth } from '../firebase';
import { doc, getDoc, getFirestore } from 'firebase/firestore';

// Firebase Functions backend URL - replace with your actual Firebase Functions URL
const API_URL = 'https://us-central1-vybecode-36007.cloudfunctions.net/api';

/**
 * Creates a Stripe checkout session for subscription
 * @returns Promise with the checkout session URL
 */
export const createCheckoutSession = async (): Promise<{ url: string }> => {
  try {
    // Get the current user
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be logged in to create a checkout session');
    }

    // Get the ID token for the current user
    const idToken = await user.getIdToken();

    // Use query parameters on root URL for success and cancel
    const successUrl = `${window.location.origin}/?subscription=success`;
    const cancelUrl = `${window.location.origin}/?subscription=cancel`;

    // Call our backend API to create a checkout session
    const response = await fetch(`${API_URL}/stripe/create-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({
        successUrl,
        cancelUrl
      })
    });

    if (!response.ok) {
      // Handle error responses
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create checkout session');
    }

    const data = await response.json();

    // Check if user is a test account that bypasses payment
    if (data.isTestAccount) {
      return { url: `${window.location.origin}/?subscription=success&test=true` };
    }

    // Return the checkout URL from Stripe
    return { url: data.url };
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
};

/**
 * Checks if a subscription was successful based on URL parameters
 * @returns Promise with the subscription verification result
 */
export const verifySubscriptionFromUrl = async (): Promise<{ success: boolean, isTestAccount?: boolean }> => {
  try {
    // Get query parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    const subscriptionStatus = urlParams.get('subscription');
    const isTestMode = urlParams.get('test') === 'true';
    
    // If there's no subscription parameter, return early
    if (subscriptionStatus !== 'success') {
      return { success: false };
    }

    // Get the current user
    const user = auth.currentUser;
    if (!user) {
      return { success: false };
    }

    // Get the ID token for the current user
    const idToken = await user.getIdToken();

    // Call our backend API to verify subscription success
    const response = await fetch(`${API_URL}/stripe/subscription/success${isTestMode ? '?test=true' : ''}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`
      }
    });

    if (!response.ok) {
      return { success: false };
    }

    const data = await response.json();
    return {
      success: data.success,
      isTestAccount: data.isTestAccount
    };
  } catch (error) {
    console.error('Error verifying subscription from URL:', error);
    return { success: false };
  }
};

/**
 * Checks if user is on the allowlist that can bypass payment
 * @returns Promise with the allowlist status
 */
export const checkAllowlistedUser = async (): Promise<{ isAllowlisted: boolean }> => {
  try {
    // Get the current user
    const user = auth.currentUser;
    if (!user) {
      return { isAllowlisted: false };
    }

    // Get the ID token for the current user
    const idToken = await user.getIdToken();

    // Call our backend API to check if user is allowlisted
    const response = await fetch(`${API_URL}/stripe/check-allowlisted-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to check allowlist status');
    }

    const data = await response.json();
    return { isAllowlisted: data.isAllowlisted };
  } catch (error) {
    console.error('Error checking allowlist status:', error);
    return { isAllowlisted: false };
  }
};

/**
 * Checks if the current user has an active subscription
 * @returns Promise with the user's subscription status
 */
export const hasActiveSubscription = async (): Promise<boolean> => {
  try {
    // Get the current user
    const user = auth.currentUser;
    if (!user) {
      return false;
    }

    // Initialize Firestore
    const db = getFirestore();
    
    // Get user document
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);
    
    // Check if user exists and has an active subscription
    if (!userDoc.exists()) {
      return false;
    }
    
    const userData = userDoc.data();
    
    // User is subscribed if they have active status or are a test account
    return (
      userData?.subscription?.status === 'active' || 
      userData?.subscription?.isTestAccount === true
    );
  } catch (error) {
    console.error('Error checking subscription status:', error);
    return false;
  }
};

/**
 * Creates a Stripe customer portal session for managing subscription
 * @returns Promise with the customer portal URL
 */
export const createCustomerPortalSession = async (): Promise<{ url: string }> => {
  try {
    // Get the current user
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be logged in to manage subscription');
    }

    // Get the ID token for the current user
    const idToken = await user.getIdToken();

    // Return URL after customer completes actions in the portal
    const returnUrl = `${window.location.origin}`;

    // Call our backend API to create a customer portal session
    const response = await fetch(`${API_URL}/stripe/create-portal-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({
        returnUrl
      })
    });

    if (!response.ok) {
      // Handle error responses
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create customer portal session');
    }

    const data = await response.json();
    return { url: data.url };
  } catch (error) {
    console.error('Error creating customer portal session:', error);
    throw error;
  }
}; 